import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { TableDashboardItemHelper } from 'src/app/components/table-dashboard-item/table-dashboard-item-helper';
import { TableColumnItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { datePartDateTrunc, getAllDatePartDateTrunc } from 'src/app/utils/sql';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { UsersdashActivityDailyDataLoaderFactory } from '../mopa-users/data-loaders/usersdash-activity-daily';
import { BiVizDashboardUsersActivity } from '../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { MopaMetaSummaryChartDocs } from './mopa-meta-summary.chart-documentation';
import { MetaSummaryByDayNoDataLoaderFactory } from './data-loaders/meta-summary-by-day-no';
import { MetaSummaryHopaDailyDataLoaderFactory } from './data-loaders/meta-summary-hopa-daily';
import { MetaSummaryTableDataLoaderFactory } from './data-loaders/meta-summary-kpi-table';

@Component({
    selector: 'app-mopa-meta-summary',
    templateUrl: './mopa-meta-summary.component.html',
    styleUrls: ['./mopa-meta-summary.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaMetaSummaryComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Meta Summary';
    override DASHBOARD_ID = 'mopa-meta-summary';
    values = Object.values;

    /** GCP Project id used in the dashboard */
    /** GCP Project id — resolved from the selected data project (URL `dataProject` param),
     * so MopaDev / MopaProd both work. Never hardcode. */
    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.meta-summary.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.meta-summary.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.meta-summary.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.meta-summary.filter.app_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.meta-summary.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-summary.filter.activity_app_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.meta-summary.filter.daily_total_energy_spent':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-summary.filter.daily_total_energy_spent',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                filterContext: true,
            }).create(),
        'mopa.meta-summary.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.meta-summary.filter.firebase_app_store',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_APP_STORE',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            })
        ).create(),
        'mopa.meta-summary.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.meta-summary.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            // filterContext: ['FIREBASE_COUNTRY'],
        }).create(),
        'mopa.meta-summary.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.meta-summary.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.meta-summary.filter.firebase_country_us_region':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-summary.filter.firebase_country_us_region',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_US_REGION',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            }).create(),
        'mopa.meta-summary.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-summary.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            }).create(),
        'mopa.meta-summary.filter.attribution_traffic_source':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.meta-summary.filter.attribution_traffic_source',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                    datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                })
            ).create(),
        'mopa.meta-summary.filter.firebase_install_version':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.meta-summary.filter.firebase_install_version',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
                    datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                    filterContext: true,
                    orderByQuery: datasourceColumnOrderByQuery(
                        'FIREBASE_INSTALL_VERSION'
                    ),
                })
            ).create(),
        'mopa.meta-summary.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.meta-summary.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.meta-summary.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.meta-summary.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
        }).create(),
        'mopa.meta-summary.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.meta-summary.filter.etl_revenue_badge',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ETL_REVENUE_BADGE',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
            })
        ).create(),
        'mopa.meta-summary.filter.attribution_network_name':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.meta-summary.filter.attribution_network_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                    datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                })
            ).create(),
        'mopa.meta-summary.filter.attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column'](
                new DatasourceColumnOldDataLoaderFactoryOptions({
                    createdInstanceId:
                        'mopa.meta-summary.filter.attribution_campaign_name',
                    gcpProjectId: this.gcpProjectId,
                    queryCacheService: this.queryCache,
                    datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                    datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                    filterContext: true,
                })
            ).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.meta-summary.modify_time_users_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId: 'mopa.meta-summary.modify_time_users_datasource',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: BiVizDashboardUsersActivity.sqlMap.dataset,
            tableId: BiVizDashboardUsersActivity.sqlMap.tableId,
        }).create(),

        'mopa.meta-summary.dau': new UsersdashActivityDailyDataLoaderFactory({
            createdInstanceId: 'mopa.meta-summary.dau',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.meta-summary.hopa_daily':
            new MetaSummaryHopaDailyDataLoaderFactory({
                createdInstanceId: 'mopa.meta-summary.hopa_daily',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'mopa.meta-summary.by_day_no': new MetaSummaryByDayNoDataLoaderFactory({
            createdInstanceId: 'mopa.meta-summary.by_day_no',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),

        'mopa.meta-summary.kpi_summary_table':
            new MetaSummaryTableDataLoaderFactory({
                createdInstanceId: 'mopa.meta-summary.kpi_summary_table',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////
    settingItemDateAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATE_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'day' as datePartDateTrunc,
            selectOptions: getAllDatePartDateTrunc(),
        },
    };

    /** PLN/USD for the KPI table revenue columns (same as mopa-sales). */
    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            value: 'pln',
            selectOptions: ['pln', 'usd'],
        },
    };

    /** Toggle between active_day / install_day for "by day_no" charts. */
    settingDayMode: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_DAY_MODE,
            viewtype: 'toggle',
            displayName: 'Day mode',
            description: 'Affects "by day_no" charts',
            value: 'install_day',
            selectOptions: [
                'install_day',
                // 'active_day'
            ],
        },
    };

    /** Limit max day_no shown in "by day_no" charts (same as Retention). */
    settingMaxDayNo: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.RETENTION_MAX_DAY_NO,
            viewtype: 'singleSelect',
            displayName: 'Limit max day no',
            value: this.getInitialContext().settings[
                QueryContextSettingID.RETENTION_MAX_DAY_NO
            ],
            selectOptions: ['60', '90', '180', '360', '720', 'unlimited'],
        },
    };

    settingShowAllUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_ALL_USERS,
            viewtype: 'checkbox',
            displayName: 'All',
            value: false,
        },
    };

    settingShowMetaUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_META_USERS,
            viewtype: 'checkbox',
            displayName: 'META',
            value: true,
        },
    };
    settingShowHopaUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_HOPA_USERS,
            viewtype: 'checkbox',
            displayName: 'HOPA',
            value: true,
        },
    };
    settingShowMetaProductiveUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS,
            viewtype: 'checkbox',
            displayName: 'META Prod.',
            value: true,
        },
    };
    settingShowCoreLoopUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_CORE_LOOP_USERS,
            viewtype: 'checkbox',
            displayName: 'CORE LOOP',
            value: true,
        },
    };

    // CHARTS
    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-summary.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.meta-summary.modify_time_users_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.meta-summary.modify_time_users_datasource'
            ].emitter,
    };

    // PROTOTYPE: DAU
    // WITHOUT DATE AGGREGATION
    dau: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.meta-summary.chart.dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.meta-summary.chart.dau',
            dashboardItem: {
                title: 'DAU',
                subtitle: 'User type checkbox',
                chartDocumentation: MopaMetaSummaryChartDocs.dau,
            },
            xColumnLabel: 'date',
            echartsSeriesType: 'line',
            metricsGetter: (context: IQueryContext) => {
                const showAll =
                    !!context.settings?.[
                        QueryContextSettingID.META_SHOW_ALL_USERS
                    ];
                const showMeta =
                    !!context.settings?.[
                        QueryContextSettingID.META_SHOW_META_USERS
                    ];
                const showHopa =
                    !!context.settings?.[
                        QueryContextSettingID.META_SHOW_HOPA_USERS
                    ];
                const showCoreLoop =
                    !!context.settings?.[
                        QueryContextSettingID.META_SHOW_CORE_LOOP_USERS
                    ];
                const showMetaProd =
                    !!context.settings?.[
                        QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS
                    ];
                return {
                    ...(showAll && {
                        all_dau: {
                            name: 'All Users',
                            echartsSeriesOption: {
                                lineStyle: { type: 'line', width: 1 },
                            },
                        },
                    }),
                    ...(showMeta && {
                        meta_dau: {
                            name: 'META Users',
                            echartsSeriesOption: {
                                lineStyle: { type: 'dotted', width: 1 },
                            },
                        },
                    }),
                    ...(showHopa && {
                        hopa_dau: {
                            name: 'HOPA Users',
                            echartsSeriesOption: {
                                lineStyle: { type: 'dashed', width: 1 },
                            },
                        },
                    }),
                    ...(showCoreLoop && {
                        core_loop_dau: {
                            name: 'CORE LOOP Users',
                            echartsSeriesOption: {
                                lineStyle: { type: [2, 5, 2, 5], width: 1 },
                            },
                        },
                    }),
                    ...(showMetaProd && {
                        meta_productive_dau: {
                            name: 'META Prod. Users',
                            echartsSeriesOption: {
                                lineStyle: { type: [4, 1, 4, 1], width: 1 },
                            },
                        },
                    }),
                };
            },
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: () =>
                    EChartsAxisHelper.axisLabelDateFormatter,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-summary.dau'].emitter,
    };

    // PROTOTYPE: Median New Decos Placed per active user by date
    // WITH DATE AGGREGATION
    median_new_decos_placed_per_active_user_by_date: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.meta-summary.chart.median_new_decos_placed_per_active_user_by_date',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.meta-summary.chart.median_new_decos_placed_per_active_user_by_date',
                dashboardItem: {
                    title: 'Median New Decos Placed per active user by date',
                    subtitle: 'Date aggregation',
                    chartDocumentation:
                        MopaMetaSummaryChartDocs.median_new_decos_placed_per_active_user_by_date,
                },
                xColumnLabel: 'date',
                echartsSeriesType: 'line',
                yColumnLabel: 'median_decos_placed',
                metricName: 'Median',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter: () =>
                        EChartsAxisHelper.axisLabelDateFormatter,
                    valueFormatterGetter: () =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter,
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.meta-summary.hopa_daily'].emitter,
        };

    // PROTOTYPE: Average New Decos Placed per active user by date
    // WITH DATE AGGREGATION
    average_new_decos_placed_per_active_user_by_date: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.meta-summary.chart.average_new_decos_placed_per_active_user_by_date',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.meta-summary.chart.average_new_decos_placed_per_active_user_by_date',
                dashboardItem: {
                    title: 'Avg. New Decos Placed per active user by date',
                    subtitle: 'Date aggregation',
                    chartDocumentation:
                        MopaMetaSummaryChartDocs.average_new_decos_placed_per_active_user_by_date,
                },
                xColumnLabel: 'date',
                echartsSeriesType: 'line',
                yColumnLabel: 'avg_decos_placed',
                metricName: 'Average',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter: () =>
                        EChartsAxisHelper.axisLabelDateFormatter,
                    valueFormatterGetter: () =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter,
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.meta-summary.hopa_daily'].emitter,
        };

    // PROTOTYPE: Median New Decos Placed per active user by install day / active day
    median_new_decos_placed_per_active_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.meta-summary.chart.median_new_decos_placed_per_active_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.meta-summary.chart.median_new_decos_placed_per_active_user_by_day_no',
                dashboardItem: {
                    title: (context: IQueryContext) =>
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Median New Decos Placed per active user by install day'
                            : 'Median New Decos Placed per active user by active day',
                    chartDocumentation:
                        MopaMetaSummaryChartDocs.median_new_decos_placed_per_active_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                echartsSeriesType: 'line',
                yColumnLabel: 'median_decos_placed',
                metricName: 'Median',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter: (context: IQueryContext) => {
                        const dayLabel =
                            context.settings?.[
                                QueryContextSettingID.META_DAY_MODE
                            ] === 'install_day'
                                ? 'Install day'
                                : 'Active day';
                        return (label: any) => `${dayLabel} ${label}`;
                    },
                    valueFormatterGetter: () =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter,
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.meta-summary.by_day_no'].emitter,
        };

    // PROTOTYPE: Average New Decos Placed per active user by install day / active day
    average_new_decos_placed_per_active_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.meta-summary.chart.average_new_decos_placed_per_active_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.meta-summary.chart.average_new_decos_placed_per_active_user_by_day_no',
                dashboardItem: {
                    title: (context: IQueryContext) =>
                        context.settings?.[
                            QueryContextSettingID.META_DAY_MODE
                        ] === 'install_day'
                            ? 'Avg. New Decos Placed per active user by install day'
                            : 'Avg. New Decos Placed per active user by active day',
                    chartDocumentation:
                        MopaMetaSummaryChartDocs.average_new_decos_placed_per_active_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                echartsSeriesType: 'line',
                yColumnLabel: 'avg_decos_placed',
                metricName: 'Average',
                colorGetterBuilder: (context: IQueryContext) => {
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter: (context: IQueryContext) => {
                        const dayLabel =
                            context.settings?.[
                                QueryContextSettingID.META_DAY_MODE
                            ] === 'install_day'
                                ? 'Install Day'
                                : 'Active Day';
                        return (label: any) => `${dayLabel} ${label}`;
                    },
                    valueFormatterGetter: () =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter,
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.meta-summary.by_day_no'].emitter,
        };

    // HL KPI Summary Table — real data from dashboard_users_activity
    // (+ dashboard_resources for median energy spent).
    kpi_summary_table = {
        id: 'lnk.mopa.meta-summary.kpi_summary_table',
        dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
            dashboardName: this.DASHBOARD_NAME,
            instanceId: 'mopa.meta-summary.kpi_summary_table',
            dashboardItem: {
                title: 'HL KPI summary table',
                viewMode: 'paged',
                chartDocumentation:
                    MopaMetaSummaryChartDocs.kpi_summary_table,
            },
            splitTableByQueryContext: true,
            // Day first, split second — date is the primary field for this table
            splitColumnsPosition: 1,
            nzPageSize: 15,
            columnsGetter: (context: IQueryContext): TableColumnItem[] => [
                {
                    id: 'date',
                    name: 'Day',
                    nzLeft: true,
                    nzAlign: 'left',
                    nzWidth: '110px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'date',
                        'string'
                    ),
                },
                {
                    id: 'dau',
                    name: 'Active Users',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'dau',
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'hopa_share',
                    name: 'HOPA Users Share (%)',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'hopa_share',
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'meta_share',
                    name: 'META Users Share (%)',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'meta_share',
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'meta_productive_share',
                    name: 'META Prod. Users Share (%)',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'meta_productive_share',
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'core_loop_share',
                    name: 'CORE LOOP Users Share (%)',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'core_loop_share',
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'median_decos_placed',
                    name: 'Median New Decos Placed per active user',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'median_decos_placed',
                        'number'
                    ),
                    labelFormatter: (v: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(v),
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'median_energy_spent',
                    name: 'Median Energy Spent by HOPA Users',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'median_energy_spent',
                        'number'
                    ),
                    labelFormatter: (v: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(v),
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'median_time_spent_min',
                    name: 'Median Time In App [min]',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'median_time_spent_min',
                        'number'
                    ),
                    labelFormatter: (v: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(v),
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'median_meta_time_min',
                    name: 'Median META Time per active user [min]',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'median_meta_time_min',
                        'number'
                    ),
                    labelFormatter: (v: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(
                            v,
                            undefined,
                            0
                        ),
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'median_meta_time_share',
                    name: 'Median META Time Share (%)',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'median_meta_time_share',
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'median_hopa_time_min',
                    name: 'Median HOPA Time per active user [min]',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'median_hopa_time_min',
                        'number'
                    ),
                    labelFormatter: (v: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(
                            v,
                            undefined,
                            0
                        ),
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'median_hopa_time_share',
                    name: 'Median HOPA Time Share (%)',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'median_hopa_time_share',
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    // The same as in dashboard Sales
                    id: 'conversion_rate',
                    name: 'Conversion Rate',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'conversion_rate',
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'arpdau',
                    name: 'ARPDAU',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'arpdau',
                        'number'
                    ),
                    labelFormatter: (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        ),
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'arppdau',
                    name: 'ARPPDAU',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'arppdau',
                        'number'
                    ),
                    labelFormatter: (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        ),
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
                {
                    id: 'revenue',
                    name: 'Revenue',
                    nzLeft: false,
                    nzAlign: 'right',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        'revenue',
                        'number'
                    ),
                    labelFormatter: (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        ),
                    colorPalette:
                        TableDashboardItemHelper.getDefaultColorPalette(),
                },
            ],
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.meta-summary.kpi_summary_table']
                .emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'mopa.meta-summary.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.meta-summary.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.meta-summary.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.meta-summary.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.activity_app_version'
                ].emitter,
        },
        DAILY_TOTAL_ENERGY_SPENT: {
            id: 'mopa.meta-summary.filter.daily_total_energy_spent',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.meta-summary.filter.daily_total_energy_spent',
                datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.daily_total_energy_spent'
                ].emitter,
        },
        SEGMENT: {
            id: 'mopa.meta-summary.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.meta-summary.filter.segments',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.meta-summary.filter.segment']
                    .emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.meta-summary.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.meta-summary.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.meta-summary.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.meta-summary.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.meta-summary.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.meta-summary.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.firebase_country'
                ].emitter,
        },
        FIREBASE_COUNTRY_US_REGION: {
            id: 'mopa.meta-summary.filter.firebase_country_us_region',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.firebase_country_us_region',
                    'FIREBASE_COUNTRY_US_REGION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.firebase_country_us_region'
                ].emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.meta-summary.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.attribution_traffic_source'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.meta-summary.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.meta-summary.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.meta-summary.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.meta-summary.filter.etl_payer']
                    .emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.meta-summary.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.etl_revenue_badge'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.meta-summary.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.meta-summary.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.meta-summary.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.meta-summary.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    override async ngOnInit() {
        super.ngOnInit();
    }

    async ngAfterViewInit() {
        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
        ];

        initialContext.settings[QueryContextSettingID.RETENTION_MAX_DAY_NO] =
            '180';
        initialContext.settings[QueryContextSettingID.META_DAY_MODE] =
            'install_day';
        initialContext.settings[QueryContextSettingID.META_SHOW_META_USERS] =
            true;
        initialContext.settings[QueryContextSettingID.META_SHOW_HOPA_USERS] =
            true;
        initialContext.settings[
            QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS
        ] = true;
        initialContext.settings[
            QueryContextSettingID.META_SHOW_CORE_LOOP_USERS
        ] = true;

        return initialContext;
    }
}
